import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { SwitchButton } from '../components/SwitchButton/SwitchButton';
import { ChangeDescriptionInput } from '../components/ChangeDescriptionInput/ChangeDescriptionInput';
import { ChangeLimitInput } from '../components/ChangeLimitInput/ChangeLimitInput';
import { ActionButtons } from '../components/ActionButtons/ActionButtons';
import { type CardsModel } from '../../../../../../../utils/types';
import {
  useApplyCardsSettingsMutation,
  useGetCardsQuery,
  useResetCardIncomeMutation,
  useResetCardsIncomeMutation,
  useToggleAllCardsMutation,
} from '../../../../../../../redux/services/CardsApi';
import mapCards from '../../../../../../../utils/functions/map/mapCards';
import { Loading } from '../../../../../../../components/Loading/Loading';
import { cardThArray } from '../../../../../../../utils/constants/th_arrays';
import { Pagination } from '../../../../../../../components/Pagination/Pagination';
import { type TCardsContentProps } from '../../CardsContent';
import { PaginationStyles, StyledTable, TableWrapperStyles } from './styles';
import { ChangeIncomeLimitInput } from '../components/ChangeIncomeLimitInput/ChangeIncomeLimitInput';
import { useFormContext } from 'react-hook-form';
import { pushNotify } from 'components/Notifications';
import { useColumnVisibility } from '../../../../../../../components/Table/useColumnVisibility';
import { parseStringToNumber } from '../../../../../../../utils/functions/parseNumber';

const PeriodLimitControl = ({
  enabledName,
  limitName,
  enabled,
  limit,
  isEditing,
}: {
  enabledName: 'newDailyLimitEnabled' | 'newMonthlyLimitEnabled';
  limitName: 'newDailyLimit' | 'newMonthlyLimit';
  enabled: boolean;
  limit: string;
  isEditing: boolean;
}) => {
  const { register } = useFormContext();

  if (!isEditing) {
    return <>{enabled ? limit || '0' : 'Выкл'}</>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
      <label style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '12px' }}>
        <input type='checkbox' {...register(enabledName)} defaultChecked={enabled} />
        Активен
      </label>
      <ChangeLimitInput
        id={1}
        currentId={1}
        defaultValue={limit}
        name={limitName}
        placeholder='0'
        rules={{ maxLength: 13 }}
      />
    </div>
  );
};

export const CardsTable: FC<TCardsContentProps> = ({ currentId, setCurrentId }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageSizeInput, setPageSizeInput] = useState('10');
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);
  const queryParams = {
    page,
    page_size: pageSize,
    search: search.trim() || undefined,
    active: activeFilter || undefined,
  };
  const { data, isLoading } = useGetCardsQuery(queryParams);
  const [toggleCards, { isLoading: isToggleLoading }] = useToggleAllCardsMutation();
  const [resetCardIncome] = useResetCardIncomeMutation();
  const [resetCardsIncome, { isLoading: isResetLoading }] = useResetCardsIncomeMutation();
  const [applyCardsSettings, { isLoading: isApplyLoading }] = useApplyCardsSettingsMutation();
  const { getValues, setValue } = useFormContext();
  const {
    isOpen,
    setIsOpen,
    pickerRef,
    visibleColumns,
    visibleCount,
    filteredThArray,
    filterRow,
    toggleColumn,
  } = useColumnVisibility('trader-cards', cardThArray);
  const cards = useMemo(() => mapCards(data?.results) || [], [data?.results]);
  const visibleIds = cards.map((card) => card.id);
  const totalPages = Math.ceil((data?.count || 0) / pageSize);
  const hasCards = Boolean(data?.total_count ?? data?.count ?? 0);
  const isActionsLoading = isToggleLoading || isResetLoading || isApplyLoading;

  const handleToggleVisible = async (status: boolean) => {
    if (!visibleIds.length) {
      return;
    }
    await toggleCards({ active: status, ids: visibleIds })
      .unwrap()
      .catch((error: any) => {
        pushNotify(error?.data?.detail || 'Не удалось изменить статус реквизитов', 'Ошибка');
      });
    setIsActionsOpen(false);
  };

  const handleToggleAll = async (status: boolean) => {
    await toggleCards({ active: status })
      .unwrap()
      .catch((error: any) => {
        pushNotify(error?.data?.detail || 'Не удалось изменить статус реквизитов', 'Ошибка');
      });
    setIsActionsOpen(false);
  };

  const handleResetVisible = async () => {
    if (!visibleIds.length) {
      return;
    }
    await resetCardsIncome({ ids: visibleIds })
      .unwrap()
      .catch((error: any) => {
        pushNotify(error?.data?.detail || 'Не удалось обнулить пополнения', 'Ошибка');
      });
    setIsActionsOpen(false);
  };

  const handleResetAll = async () => {
    await resetCardsIncome({})
      .unwrap()
      .catch((error: any) => {
        pushNotify(error?.data?.detail || 'Не удалось обнулить пополнения', 'Ошибка');
      });
    setIsActionsOpen(false);
  };

  const handleApplySettingsAll = async () => {
    if (!currentId) {
      return;
    }
    const currencyCode = data?.results.find((card) => card.id === currentId)?.currency_code;
    if (!currencyCode) {
      pushNotify('Не удалось определить валюту реквизита', 'Ошибка');
      return;
    }

    const values = getValues();
    await applyCardsSettings({
      currency_code: currencyCode,
      total_limit: parseStringToNumber(values.newLimit),
      min_income: +values.newMinIncome,
      max_income: +values.newMaxIncome,
      instant_active_orders: parseStringToNumber(values.newInstantOrders),
      daily_total_limit: parseStringToNumber(values.newDailyLimit || '0'),
      monthly_total_limit: parseStringToNumber(values.newMonthlyLimit || '0'),
      daily_limit_enabled: !!values.newDailyLimitEnabled,
      monthly_limit_enabled: !!values.newMonthlyLimitEnabled,
    })
      .unwrap()
      .catch((error: any) => {
        pushNotify(error?.data?.detail || 'Не удалось применить настройки ко всем реквизитам', 'Ошибка');
      });
    setIsActionsOpen(false);
  };

  useEffect(() => {
    setPage(1);
  }, [search, activeFilter, pageSize]);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!actionsMenuRef.current?.contains(event.target as Node)) {
        setIsActionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, []);

  useEffect(() => {
    setPageSizeInput(String(pageSize));
  }, [pageSize]);

  useEffect(() => {
    const currentCard = cards.find((card) => card.id === currentId);
    if (!currentCard) {
      return;
    }

    setValue('newDailyLimit', String(currentCard.daily_total_limit || '0'));
    setValue('newMonthlyLimit', String(currentCard.monthly_total_limit || '0'));
    setValue('newDailyLimitEnabled', !!currentCard.daily_limit_enabled);
    setValue('newMonthlyLimitEnabled', !!currentCard.monthly_limit_enabled);
  }, [cards, currentId, setValue]);

  const handleResetIncome = async (cardId: number) => {
    await resetCardIncome(cardId)
      .unwrap()
      .catch((error: any) => {
        pushNotify(error?.data?.detail || 'Не удалось обнулить пополнения', 'Ошибка');
      });
  };

  return isLoading ? (
    <Loading />
  ) : (
    <>
      <div className='cards_toolbar'>
        <div className='cards_stats'>
          <span>Всего: {data?.total_count ?? data?.count ?? 0}</span>
          <span>Включено: {data?.active_count ?? 0}</span>
          <span>Выключено: {data?.inactive_count ?? 0}</span>
        </div>
        <div className='cards_controls'>
          <input
            className='cards_search'
            type='search'
            placeholder='Поиск по названию'
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            }}
          />
          <select
            className='cards_select'
            value={activeFilter}
            onChange={(event) => setActiveFilter(event.target.value)}
          >
            <option value=''>Все</option>
            <option value='true'>Включенные</option>
            <option value='false'>Выключенные</option>
          </select>
          <input
            className='cards_select'
            type='number'
            min={1}
            max={500}
            value={pageSizeInput}
            onChange={(event) => setPageSizeInput(event.target.value)}
            onBlur={() => {
              const parsed = Number(pageSizeInput);
              const nextValue = Number.isFinite(parsed) ? Math.min(Math.max(parsed, 1), 500) : 10;
              setPageSize(nextValue);
              setPageSizeInput(String(nextValue));
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                const parsed = Number(pageSizeInput);
                const nextValue = Number.isFinite(parsed) ? Math.min(Math.max(parsed, 1), 500) : 10;
                setPageSize(nextValue);
                setPageSizeInput(String(nextValue));
              }
            }}
          />
          <div style={{ position: 'relative' }} ref={pickerRef}>
            <button
              type='button'
              className='cards_page_toggle secondary'
              onClick={() => setIsOpen(!isOpen)}
            >
              Столбцы
            </button>
            {isOpen && (
              <div className='table_columns_dropdown'>
                {cardThArray.map((th, index) => (
                  <label key={`${th.name}-${index}`} className='table_columns_option'>
                    <input
                      type='checkbox'
                      checked={visibleColumns[index] ?? true}
                      disabled={(visibleColumns[index] ?? true) && visibleCount === 1}
                      onChange={() => toggleColumn(index)}
                    />
                    <span>{th.name || `Столбец ${index + 1}`}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className='cards_actions' ref={actionsMenuRef}>
            <button
              type='button'
              className='cards_actions_button'
              disabled={isActionsLoading}
              onClick={() => setIsActionsOpen(!isActionsOpen)}
            >
              Действия
            </button>
            {isActionsOpen && (
              <div className='cards_actions_menu'>
                <div className='cards_actions_group'>Страница</div>
                <button
                  type='button'
                  className='cards_actions_item'
                  disabled={!visibleIds.length || isActionsLoading}
                  onClick={() => handleToggleVisible(true)}
                >
                  Включить страницу
                </button>
                <button
                  type='button'
                  className='cards_actions_item'
                  disabled={!visibleIds.length || isActionsLoading}
                  onClick={() => handleToggleVisible(false)}
                >
                  Выключить страницу
                </button>
                <button
                  type='button'
                  className='cards_actions_item warning'
                  disabled={!visibleIds.length || isActionsLoading}
                  onClick={handleResetVisible}
                >
                  Обнулить страницу
                </button>
                <div className='cards_actions_group'>Все реквизиты</div>
                <button
                  type='button'
                  className='cards_actions_item'
                  disabled={!hasCards || isActionsLoading}
                  onClick={() => handleToggleAll(true)}
                >
                  Включить все
                </button>
                <button
                  type='button'
                  className='cards_actions_item'
                  disabled={!hasCards || isActionsLoading}
                  onClick={() => handleToggleAll(false)}
                >
                  Выключить все
                </button>
                <button
                  type='button'
                  className='cards_actions_item warning'
                  disabled={!hasCards || isActionsLoading}
                  onClick={handleResetAll}
                >
                  Обнулить все
                </button>
                <div className='cards_actions_group'>Редактирование</div>
                <button
                  type='button'
                  className='cards_actions_item accent'
                  disabled={!currentId || !hasCards || isActionsLoading}
                  onClick={handleApplySettingsAll}
                >
                  Применить настройки ко всем
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <TableWrapperStyles>
        <StyledTable>
          <thead>
            <tr>
              {filteredThArray.map((th, index) => (
                <th
                  key={index}
                  style={{
                    ...th.style,
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    maxWidth: '250px',
                    backgroundColor: '#e2e5f0',
                    fontWeight: '400',
                  }}
                >
                  {th.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cards.map((card: CardsModel) => {
              const row = [
                { element: <SwitchButton active={card.active} id={card.id} /> },
                { element: card.tag },
                {
                  element: (
                    <ChangeDescriptionInput
                      id={card.id}
                      currentId={currentId}
                      defaultValue={card.description}
                      placeholder={''}
                      name={'newDescription'}
                    />
                  ),
                },
                { element: card.payment_type },
                {
                  element:
                    card.payment_type === 'C2C' || card.payment_type === 'SBP' || card.payment_type === 'C2C,SBP'
                      ? card.account_number
                      : <>-</>,
                },
                { element: card.payment_type === 'TRANSFER' ? card.account_number : <>-</> },
                { element: card.bank.bic ?? <>-</> },
                { element: card.bank.name },
                { element: card.payment_type === 'TRANSFER' ? 'Перевод' : '-' },
                { element: card.phone_number },
                {
                  element: (
                    <ChangeLimitInput
                      currentId={currentId}
                      id={card.id}
                      defaultValue={card.total_limit}
                      name='newLimit'
                      placeholder={'Лимит карты'}
                    />
                  ),
                },
                { element: card.fio },
                { element: card.total_income },
                {
                  element: (
                    <button
                      type='button'
                      style={{ padding: '6px 10px' }}
                      onClick={() => handleResetIncome(card.id)}
                    >
                      Обнулить
                    </button>
                  ),
                },
                {
                  element: (
                    <ChangeIncomeLimitInput
                      currentId={currentId}
                      id={card.id}
                      defaultValue={String(card.min_income)}
                      name='newMinIncome'
                      placeholder={''}
                    />
                  ),
                },
                {
                  element: (
                    <ChangeIncomeLimitInput
                      currentId={currentId}
                      id={card.id}
                      defaultValue={String(card.max_income)}
                      name='newMaxIncome'
                      placeholder={''}
                    />
                  ),
                },
                {
                  element: (
                    <ChangeLimitInput
                      currentId={currentId}
                      id={card.id}
                      defaultValue={String(card.instant_active_orders)}
                      name='newInstantOrders'
                      placeholder={''}
                      rules={{ min: 1, max: 100000000, maxLength: 11 }}
                    />
                  ),
                },
                {
                  element: (
                    <PeriodLimitControl
                      enabledName='newDailyLimitEnabled'
                      limitName='newDailyLimit'
                      enabled={!!card.daily_limit_enabled}
                      limit={String(card.daily_total_limit || '0')}
                      isEditing={card.id === currentId}
                    />
                  ),
                },
                {
                  element: (
                    <PeriodLimitControl
                      enabledName='newMonthlyLimitEnabled'
                      limitName='newMonthlyLimit'
                      enabled={!!card.monthly_limit_enabled}
                      limit={String(card.monthly_total_limit || '0')}
                      isEditing={card.id === currentId}
                    />
                  ),
                },
                { element: <ActionButtons currentId={currentId} setCurrentId={setCurrentId} id={card.id} /> },
              ];

              return (
                <tr key={card.id}>
                  {filterRow(row).map((cell, index) => (
                    <td key={index}>{cell.element}</td>
                  ))}
                </tr>
              );
            })}
            {!cards.length && (
              <tr>
                <td colSpan={filteredThArray.length}>Реквизиты не найдены</td>
              </tr>
            )}
          </tbody>
        </StyledTable>
      </TableWrapperStyles>
      <PaginationStyles>
        <Pagination
          totalPages={totalPages}
          setCurrentPage={setPage}
          currentPage={page}
          nextPage={data?.next || page}
          previousPage={data?.previous || page}
        />
      </PaginationStyles>
    </>
  );
};
